import type { PropType } from 'virtual-vue';
import type { HalfScreenDialogAction, HalfScreenDialogActionsLayout } from './half-screen-dialog';

export const DEFAULT_HALF_SCREEN_DIALOG_VISIBLE = false;
export const DEFAULT_HALF_SCREEN_DIALOG_TITLE = '';
export const DEFAULT_HALF_SCREEN_DIALOG_SUB_TITLE = '';
export const DEFAULT_HALF_SCREEN_DIALOG_SHOW_CLOSE = true;
export const DEFAULT_HALF_SCREEN_DIALOG_CLOSE_ON_CLICK_OVERLAY = true;
export const DEFAULT_HALF_SCREEN_DIALOG_SHOW_FOOTER_GRADIENT = false;
export const DEFAULT_HALF_SCREEN_DIALOG_LOCK_SCROLL = true;
export const DEFAULT_HALF_SCREEN_DIALOG_OVERLAY_CLASS = '';
export const DEFAULT_HALF_SCREEN_DIALOG_DESTROY_ON_CLOSE = false;
export const DEFAULT_HALF_SCREEN_DIALOG_TELEPORT = 'body';
export const DEFAULT_HALF_SCREEN_DIALOG_CLOSE_ARIA_LABEL = '关闭弹窗';
export const DEFAULT_HALF_SCREEN_DIALOG_LAZY_RENDER = true;
export const DEFAULT_HALF_SCREEN_DIALOG_SCROLLABLE = true;
export const DEFAULT_HALF_SCREEN_DIALOG_ACTIONS: HalfScreenDialogAction[] = [];
export const DEFAULT_HALF_SCREEN_DIALOG_ACTIONS_LAYOUT: HalfScreenDialogActionsLayout = 'horizontal';

/**
 * 组件的props
 * 基于方案设计文档生成
 */
export const props = {
  /**
   * 是否显示弹窗
   * @default false
   */
  visible: {
    type: Boolean,
    default: DEFAULT_HALF_SCREEN_DIALOG_VISIBLE,
  },
  /**
   * 弹窗标题
   * @default ''
   */
  title: {
    type: String,
    default: DEFAULT_HALF_SCREEN_DIALOG_TITLE,
  },
  /**
   * 副标题/辅助说明文本（显示在标题下方）
   * @default ''
   */
  subTitle: {
    type: String,
    default: DEFAULT_HALF_SCREEN_DIALOG_SUB_TITLE,
  },
  /**
   * 是否显示关闭按钮
   * @default true
   */
  showClose: {
    type: Boolean,
    default: DEFAULT_HALF_SCREEN_DIALOG_SHOW_CLOSE,
  },
  /**
   * 点击遮罩层是否关闭弹窗
   * @default true
   */
  closeOnClickOverlay: {
    type: Boolean,
    default: DEFAULT_HALF_SCREEN_DIALOG_CLOSE_ON_CLICK_OVERLAY,
  },
  /**
   * 是否显示底部渐变遮罩（当内容过长时提示用户滚动）
   * @default false
   */
  showFooterGradient: {
    type: Boolean,
    default: DEFAULT_HALF_SCREEN_DIALOG_SHOW_FOOTER_GRADIENT,
  },
  /**
   * 是否锁定背景滚动
   * @default true
   */
  lockScroll: {
    type: Boolean,
    default: DEFAULT_HALF_SCREEN_DIALOG_LOCK_SCROLL,
  },
  /**
   * 遮罩层自定义类名
   * @default ''
   */
  overlayClass: {
    type: String,
    default: DEFAULT_HALF_SCREEN_DIALOG_OVERLAY_CLASS,
  },
  /**
   * 关闭时是否销毁子元素
   * @default false
   */
  destroyOnClose: {
    type: Boolean,
    default: DEFAULT_HALF_SCREEN_DIALOG_DESTROY_ON_CLOSE,
  },
  /**
   * 指定挂载的节点
   * @default 'body'
   */
  teleport: {
    type: String,
    default: DEFAULT_HALF_SCREEN_DIALOG_TELEPORT,
  },
  /**
   * 关闭前的回调函数，返回 false 可阻止关闭
   */
  beforeClose: {
    type: Function as PropType<(action: 'close' | 'overlay') => boolean | Promise<boolean>>,
  },
  /**
   * 关闭按钮的 aria-label 文本（无障碍）
   * @default '关闭弹窗'
   */
  closeAriaLabel: {
    type: String,
    default: DEFAULT_HALF_SCREEN_DIALOG_CLOSE_ARIA_LABEL,
  },
  /**
   * 是否在首次显示时才渲染内容
   * @default true
   */
  lazyRender: {
    type: Boolean,
    default: DEFAULT_HALF_SCREEN_DIALOG_LAZY_RENDER,
  },
  /**
   * 内容区域是否可滚动，设为 false 时禁止内容区滚动
   * 适用于嵌套自带滚动能力的子组件（如 Picker、DatePicker）
   * @default true
   */
  scrollable: {
    type: Boolean,
    default: DEFAULT_HALF_SCREEN_DIALOG_SCROLLABLE,
  },
  /**
   * 底部按钮配置数组（0-2个）
   * @default []
   */
  actions: {
    type: Array as PropType<HalfScreenDialogAction[]>,
    default: () => DEFAULT_HALF_SCREEN_DIALOG_ACTIONS,
  },
  /**
   * 按钮排列方式
   * @default 'horizontal'
   */
  actionsLayout: {
    type: String as PropType<HalfScreenDialogActionsLayout>,
    default: DEFAULT_HALF_SCREEN_DIALOG_ACTIONS_LAYOUT,
  },
};

